/**
 * campaigns.service.ts
 *
 * Typed API call wrappers for the Email Campaigns domain.
 * All methods use the shared apiClient (Axios instance with JWT interceptor).
 *
 * The API returns raw DB columns in snake_case; this service maps them to
 * the camelCase Campaign interface used by the frontend.
 */
import { apiClient } from './apiClient';
import type { PaginatedResponse } from '@/types/api.types';
import type { ContactLead, ContactLeadParam } from './marketing.service';

// ─── Domain types ─────────────────────────────────────────────────────────────

export interface Campaign {
  campaignId: number;
  accountId: number;
  name: string;
  subject: string;
  preheader: string | null;
  description: string | null;
  emailBody: string | null;
  plainContent: string | null;
  /** DnD email model JSON when the campaign was authored with the DnD editor. */
  json: string | null;
  emailTemplateId: number | null;
  fromName: string | null;
  fromEmail: string | null;
  replyTo: string | null;
  status: string;
  scheduledAt: string | null;
  sentAt: string | null;
  totalRecipients: number;
  recipientType: string | null;
  createdBy: number | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string | null;
  uniqueOpens: number;
  uniqueClicks: number;
}

export interface CampaignListParams {
  page?: number;
  pageSize?: number;
  status?: string;
  search?: string;
}

export interface CreateCampaignData {
  name: string;
  subject: string;
  preheader?: string | null;
  description?: string | null;
  emailBody?: string | null;
  plainContent?: string | null;
  json?: string | null;
  emailTemplateId?: number | null;
  fromName?: string | null;
  fromEmail?: string | null;
  replyTo?: string | null;
  status?: string;
  scheduledAt?: string | null;
  recipientType?: string | null;
  accountUsers?: number;
}

export interface UpdateCampaignData extends Partial<CreateCampaignData> {}

export interface CampaignListStats {
  total: number;
  drafts: number;
  scheduled: number;
  sent: number;
  totalRecipients: number;
}

export interface CampaignList {
  emailListId: number;
  name: string;
  listType: string;
  patientCount: number;
  uniqueEmailCount?: number;
  uniquePhoneCount?: number;
  active: number;
  attachedAt: string;
}

export interface CampaignContact {
  emailCampaignContactID: number;
  campaignID: number;
  email: string;
  firstName: string;
  lastName: string;
  pFirstName?: string | null;
  pLastName?: string | null;
  deliveryStatus: string;
  createDate: string;
  patientID: number | null;
  open: number;
  clicks: number;
  spamReport: number;
  unsubscribe: number;
  /** 1 when this contact replied to the campaign. Derived server-side. */
  replied?: number;
  /** Request-info leads this contact's email produced. Absent on older responses. */
  leads?: ContactLead[];
  reason?: string;
  recordStatus: number;
}

export interface CampaignContactReply {
  id: number;
  subject: string;
  preview: string;
  /** Full comm_log body when the reply was logged, else the short excerpt. */
  body: string;
  bodyIsFull: boolean;
  receivedDate: string;
  hadAttachments: boolean;
  commLogId: number;
  patientId: number;
}

export interface CampaignExclude {
  emailCampaignExcludeID: number;
  campaignID: number;
  email: string;
  cellPhone: string;
  createDate: string;
  patientID: number | null;
  status: number;
}

export interface AddContactData {
  email?: string;
  first_name?: string;
  last_name?: string;
  patient_id?: number;
}

export interface AddExcludeData {
  email?: string;
  cell_phone?: string;
  patient_id?: number;
}

export interface SendCampaignPayload {
  name: string;
  subject: string;
  preheader?: string;
  from_name: string;
  from_email: string;
  reply_to?: string;
  html_content: string;
  plain_content?: string;
  description?: string;
  email_template_id?: number | null;
  accountUsers?: number;
}

export interface ScheduleCampaignPayload extends SendCampaignPayload {
  scheduled_at: string;
}

export interface TestEmailData {
  test_email: string;
  subject: string;
  preheader?: string;
  from_email: string;
  from_name: string;
  html_content: string;
  plain_content?: string;
}

// ─── Mappers ──────────────────────────────────────────────────────────────────

function mapRawToCampaign(raw: Record<string, any>): Campaign {
  return {
    campaignId: raw.id ?? raw.campaignId ?? 0,
    accountId: raw.accountID ?? raw.accountId ?? 0,
    name: raw.name ?? '',
    subject: raw.subject ?? '',
    preheader: raw.preheader ?? null,
    description: raw.description ?? null,
    emailBody: raw.html_content ?? raw.emailBody ?? null,
    plainContent: raw.plain_content ?? raw.plainContent ?? null,
    json: raw.json_content ?? raw.json ?? null,
    emailTemplateId: raw.email_template_id ?? raw.emailTemplateId ?? null,
    fromName: raw.from_name ?? raw.fromName ?? null,
    fromEmail: raw.from_email ?? raw.fromEmail ?? null,
    replyTo: raw.reply_to ?? raw.replyTo ?? null,
    status: raw.status ?? 'draft',
    scheduledAt: raw.scheduled_at ?? raw.scheduledAt ?? null,
    sentAt: raw.sent_at ?? raw.sentAt ?? null,
    totalRecipients: raw.total_recipients ?? raw.totalRecipients ?? 0,
    recipientType: raw.recipient_type ?? raw.recipientType ?? null,
    createdBy: raw.created_by ?? raw.createdBy ?? null,
    createdByName: raw.createdByName ?? raw.created_by_name ?? null,
    createdAt: raw.created_at ?? raw.createdAt ?? '',
    updatedAt: raw.updated_at ?? raw.updatedAt ?? null,
    uniqueOpens: raw.unique_opens ?? raw.uniqueOpens ?? 0,
    uniqueClicks: raw.unique_clicks ?? raw.uniqueClicks ?? 0,
  };
}

function mapCampaignToSnake(data: UpdateCampaignData): Record<string, any> {
  const mapped: Record<string, any> = {};
  if (data.name !== undefined) mapped.name = data.name;
  if (data.subject !== undefined) mapped.subject = data.subject;
  if (data.preheader !== undefined) mapped.preheader = data.preheader;
  if (data.description !== undefined) mapped.description = data.description;
  if (data.emailBody !== undefined) mapped.html_content = data.emailBody;
  if (data.plainContent !== undefined) mapped.plain_content = data.plainContent;
  if (data.json !== undefined) mapped.json_content = data.json;
  if (data.emailTemplateId !== undefined) mapped.email_template_id = data.emailTemplateId;
  if (data.fromName !== undefined) mapped.from_name = data.fromName;
  if (data.fromEmail !== undefined) mapped.from_email = data.fromEmail;
  if (data.replyTo !== undefined) mapped.reply_to = data.replyTo;
  if (data.status !== undefined) mapped.status = data.status;
  if (data.scheduledAt !== undefined) mapped.scheduled_at = data.scheduledAt;
  if (data.accountUsers !== undefined) mapped.accountUsers = data.accountUsers;
  return mapped;
}

// ─── API methods ──────────────────────────────────────────────────────────────

export function getCampaignStats(): Promise<CampaignListStats> {
  return apiClient
    .get<{ data: CampaignListStats }>('/campaigns/stats')
    .then((r) => r.data.data);
}

export function listCampaigns(
  params?: CampaignListParams,
): Promise<PaginatedResponse<Campaign>> {
  return apiClient
    .get('/campaigns', { params })
    .then((r) => {
      const payload = r.data;
      const rawItems: any[] = payload?.data ?? payload ?? [];
      const campaigns = rawItems.map(mapRawToCampaign);
      return {
        data: campaigns,
        meta: payload?.meta ?? { page: 1, perPage: 25, total: campaigns.length },
      };
    });
}

export function getCampaign(id: number): Promise<Campaign> {
  return apiClient
    .get(`/campaigns/${id}`)
    .then((r) => {
      const raw = r.data?.data ?? r.data;
      return mapRawToCampaign(raw);
    });
}

export function createCampaign(data: CreateCampaignData): Promise<Campaign & { campaignId: number }> {
  const payload = mapCampaignToSnake(data);
  return apiClient
    .post('/campaigns', payload)
    .then((r) => {
      const raw = r.data?.data ?? r.data;
      return {
        ...mapRawToCampaign({ ...payload, id: raw.id ?? raw.campaignId }),
        campaignId: raw.id ?? raw.campaignId ?? 0,
      };
    });
}

export function updateCampaign(id: number, data: UpdateCampaignData): Promise<Campaign> {
  const payload = mapCampaignToSnake(data);
  return apiClient
    .patch(`/campaigns/${id}`, payload)
    .then((r) => {
      const raw = r.data?.data ?? r.data;
      if (raw?.id || raw?.campaignId) {
        return mapRawToCampaign(raw);
      }
      return mapRawToCampaign({ id, ...payload });
    });
}

export function deleteCampaign(id: number): Promise<void> {
  return apiClient.delete(`/campaigns/${id}`).then(() => undefined);
}

// ─── Lists ──────────────────────────────────────────────────────────────────

export function getCampaignLists(id: number): Promise<CampaignList[]> {
  return apiClient
    .get(`/campaigns/${id}/lists`)
    .then((r) => (r.data?.data ?? r.data ?? []) as CampaignList[]);
}

export function addCampaignList(id: number, listId: number): Promise<{ total_recipients: number }> {
  return apiClient
    .post(`/campaigns/${id}/lists`, { listId })
    .then((r) => r.data?.data ?? r.data);
}

export function removeCampaignList(id: number, listId: number): Promise<{ total_recipients: number }> {
  return apiClient
    .delete(`/campaigns/${id}/lists/${listId}`)
    .then((r) => r.data?.data ?? r.data);
}

// ─── Direct Contacts ────────────────────────────────────────────────────────

export interface CampaignContactStatusCounts {
  delivered: number;
  pending: number;
  queued: number;
  failed: number;
  invalid: number;
  bounce: number;
  dropped: number;
  opened: number;
  not_opened: number;
  clicked: number;
  replied: number;
  unsubscribed: number;
  /** Contacts who marked the email as spam. */
  spam?: number;
  /** Contacts whose email produced a request-info lead, either kind, then each kind. */
  lead?: number;
  leadRecipient?: number;
  leadReferral?: number;
}

export function getCampaignContacts(
  id: number,
  params?: { search?: string; page?: number; perPage?: number; showDeleted?: boolean; contactFilter?: string; lead?: ContactLeadParam },
): Promise<{ data: CampaignContact[]; meta: { page: number; perPage: number; total: number }; statusCounts: CampaignContactStatusCounts }> {
  return apiClient
    .get(`/campaigns/${id}/contacts`, { params })
    .then((r) => {
      const payload = r.data;
      const outerMeta = payload?.meta ?? {};
      return {
        data: payload?.data ?? [],
        meta: outerMeta?.meta ?? { page: 1, perPage: 25, total: 0 },
        statusCounts: outerMeta?.statusCounts ?? { delivered: 0, pending: 0, queued: 0, failed: 0, invalid: 0, bounce: 0, dropped: 0, opened: 0, not_opened: 0, clicked: 0, unsubscribed: 0 },
      };
    });
}

export function addCampaignContact(id: number, data: AddContactData): Promise<{ total_recipients: number }> {
  return apiClient
    .post(`/campaigns/${id}/contacts`, data)
    .then((r) => r.data?.data ?? r.data);
}

export function removeCampaignContact(id: number, contactId: number): Promise<{ total_recipients: number }> {
  return apiClient
    .delete(`/campaigns/${id}/contacts/${contactId}`)
    .then((r) => r.data?.data ?? r.data);
}

export function restoreCampaignContact(id: number, contactId: number): Promise<{ total_recipients: number }> {
  return apiClient
    .post(`/campaigns/${id}/contacts/${contactId}/restore`)
    .then((r) => r.data?.data ?? r.data);
}

export function clearCampaignContacts(id: number): Promise<{ total_recipients: number }> {
  return apiClient
    .post(`/campaigns/${id}/contacts/clear`)
    .then((r) => r.data?.data ?? r.data);
}

export function getCampaignContactCount(id: number): Promise<number> {
  return apiClient
    .get(`/campaigns/${id}/contact-count`)
    .then((r) => r.data?.data?.count ?? 0);
}

// ─── Individual Contact Stats ───────────────────────────────────────────────

export interface ContactStatsResponse {
  contact: {
    email: string;
    firstName: string;
    lastName: string;
    patientID: number | null;
    status: string;
    reason: string;
    sentDate: string;
    /** Last status write — the only timestamp available for failed/invalid sends. */
    updateDate?: string;
  };
  delivery: Record<
    string,
    { event_type: string; first_at: string; last_at: string; total: number }
  >;
  opens: { total_opens: number; first_open: string | null; last_open: string | null };
  clicks: { total_clicks: number; first_click: string | null; last_click: string | null };
  urlClicks: {
    url: string;
    total_clicks: number;
    first_click: string | null;
    last_click: string | null;
  }[];
  /** Replies this contact sent to the campaign. Absent on older responses. */
  replies?: CampaignContactReply[];
  /** Request-info leads this email produced. Absent on older responses. */
  leads?: ContactLead[];
}

export function getContactStats(
  campaignId: number,
  contactId: number,
): Promise<ContactStatsResponse> {
  return apiClient
    .get(`/campaigns/${campaignId}/contacts/${contactId}/stats`)
    .then((r) => r.data?.data ?? r.data);
}

// ─── Excludes ───────────────────────────────────────────────────────────────

export function getCampaignExcludes(
  id: number,
  params?: { search?: string; page?: number; perPage?: number; showDeleted?: boolean },
): Promise<{ data: CampaignExclude[]; meta: { page: number; perPage: number; total: number } }> {
  return apiClient
    .get(`/campaigns/${id}/excludes`, { params })
    .then((r) => {
      const payload = r.data;
      return {
        data: payload?.data ?? [],
        meta: payload?.meta ?? { page: 1, perPage: 25, total: 0 },
      };
    });
}

export function addCampaignExclude(id: number, data: AddExcludeData): Promise<{ total_recipients: number }> {
  return apiClient
    .post(`/campaigns/${id}/excludes`, data)
    .then((r) => r.data?.data ?? r.data);
}

export function removeCampaignExclude(id: number, excludeId: number): Promise<{ total_recipients: number }> {
  return apiClient
    .delete(`/campaigns/${id}/excludes/${excludeId}`)
    .then((r) => r.data?.data ?? r.data);
}

export function restoreCampaignExclude(id: number, excludeId: number): Promise<{ total_recipients: number }> {
  return apiClient
    .post(`/campaigns/${id}/excludes/${excludeId}/restore`)
    .then((r) => r.data?.data ?? r.data);
}

export function clearCampaignExcludes(id: number): Promise<{ total_recipients: number }> {
  return apiClient
    .post(`/campaigns/${id}/excludes/clear`)
    .then((r) => r.data?.data ?? r.data);
}

export function getCampaignExcludeCount(id: number): Promise<number> {
  return apiClient
    .get(`/campaigns/${id}/exclude-count`)
    .then((r) => r.data?.data?.count ?? 0);
}

// ─── Recipient Count ────────────────────────────────────────────────────────

export function getRecipientCount(id: number): Promise<number> {
  return apiClient
    .get(`/campaigns/${id}/recipient-count`)
    .then((r) => r.data?.data?.count ?? 0);
}

// ─── Toggle Account Users ───────────────────────────────────────────────────

export function toggleAccountUsers(
  id: number,
  enabled: boolean,
): Promise<{ total_recipients: number; accountUsers: number }> {
  return apiClient
    .post(`/campaigns/${id}/toggle-account-users`, { enabled })
    .then((r) => r.data?.data ?? r.data);
}

// ─── Send / Schedule / Cancel ───────────────────────────────────────────────

export function sendCampaign(id: number, payload: SendCampaignPayload): Promise<any> {
  return apiClient
    .post(`/campaigns/${id}/send`, payload)
    .then((r) => r.data?.data ?? r.data);
}

export function scheduleCampaign(id: number, payload: ScheduleCampaignPayload): Promise<any> {
  return apiClient
    .post(`/campaigns/${id}/schedule`, payload)
    .then((r) => r.data?.data ?? r.data);
}

export function cancelSchedule(id: number): Promise<any> {
  return apiClient
    .post(`/campaigns/${id}/cancel-schedule`)
    .then((r) => r.data?.data ?? r.data);
}

export interface EmailResumeResponse {
  success: boolean;
  message: string;
  queuedCount: number;
  stuckSendingReset: number;
}

/** Re-dispatch a partially-sent campaign's remaining queued contacts (level 5+). */
export function resumeEmailCampaign(id: number): Promise<EmailResumeResponse> {
  return apiClient
    .post(`/campaigns/${id}/resume`)
    .then((r) => r.data?.data ?? r.data);
}

// ─── Test Email ─────────────────────────────────────────────────────────────

export function sendTestEmail(id: number, data: TestEmailData): Promise<any> {
  return apiClient
    .post(`/campaigns/${id}/test`, data)
    .then((r) => r.data?.data ?? r.data);
}

// ─── Clone Campaign ─────────────────────────────────────────────────────────

export function cloneCampaign(id: number, name: string): Promise<{ id: number }> {
  return apiClient
    .post(`/campaigns/${id}/clone`, { name })
    .then((r) => r.data?.data ?? r.data);
}
